import { HttpClient } from '@angular/common/http';
import { Injectable, Optional, Renderer2, SkipSelf } from '@angular/core';
import * as Rx from 'rxjs';
import { catchError, finalize, first, map, share, tap } from 'rxjs/operators';

@Injectable()
export class IconService {
  private inProgressUrlFetches = new Map<string, Rx.Observable<string>>();
  private cachedIconsByUrl = new Map<string, SVGElement>();

  constructor(private http: HttpClient) {}

  /**
   * Fetch a URL to get an icon SVG string
   *
   * @private
   * @param {string} url The URL to the SVG
   * @returns {string} The SVG markup
   *
   * @memberof IconService
   */
  private fetchUrl(url: string): Rx.Observable<string> {
    // Store in-progress fetches to avoid sending a duplicate
    // request for a URL when there is already a request in progress for that
    //  URL. It's necessary to call share() on the Rx.Observable returned by
    // http.get() so that multiple subscribers don't cause multiple XHRs.
    const inProgressUrlFetch$ = this.inProgressUrlFetches.get(url);
    if (inProgressUrlFetch$) {
      return inProgressUrlFetch$;
    }

    const request$ = this.http.get(url, { responseType: 'text' }).pipe(
      first(),
      finalize(() => {
        this.inProgressUrlFetches.delete(url);
      }),
      share(),
    );

    this.inProgressUrlFetches.set(url, request$);

    return request$;
  }

  /**
   * Create an SVG element from an SVG markup
   *
   * @private
   * @param {string} svgString The SVG markup
   * @returns {SVGElement} The SVGElement Node rendered
   *
   * @memberof IconService
   */
  private createSvgElement(markup: string, renderer: Renderer2) {
    const div = <HTMLDivElement>renderer.createElement('div');
    // TODO T37429 Is there a better way than innerHTML? Renderer doesn't appear
    // to have a method for creating an element from an HTML string.
    div.innerHTML = markup;
    const svg = <SVGElement>div.querySelector('svg');

    if (!svg) {
      throw Error(`<svg> tag not found. Markup is ${markup.slice(0, 100)}.`);
    }

    return svg;
  }

  /**
   * Set the necessary SVG attributes.
   *
   * @private
   * @param {SVGElement} svg The SVG element
   * @memberof IconService
   */
  private setSvgAttributes(svg: SVGElement) {
    if (!svg.getAttribute('xmlns')) {
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    }

    svg.removeAttribute('class');
    svg.setAttribute('fit', '');
    svg.setAttribute('height', '100%');
    svg.setAttribute('width', '100%');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    // Disable IE11 default behavior to make SVGs focusable.
    svg.setAttribute('focusable', 'false');

    return svg;
  }

  /**
   * Get an icon SVG element
   *
   * @param {string} url The URL of the SVG element
   * @returns {SVGElement} The SVG element.
   *
   * @memberof IconService
   */
  getIcon(url: string, renderer: Renderer2): Rx.Observable<SVGElement> {
    const cachedIcon = this.cachedIconsByUrl.get(url);
    if (cachedIcon) {
      return Rx.of(cloneSvg(cachedIcon));
    }

    return this.fetchUrl(url).pipe(
      map(markup => this.createSvgElement(markup, renderer)),
      catchError((err: Error) =>
        Rx.throwError(
          Error(`Cannot render icon for ${url}. Error was ${err.message}.`),
        ),
      ),
      map(svg => this.setSvgAttributes(svg)),
      tap(svg => this.cachedIconsByUrl.set(url, svg)),
      map(svg => cloneSvg(svg)),
    );
  }
}

/**
 * Provider factory for Icon Service
 *
 * @export
 * @param {IconService} parentRegistry The parent registry
 * @param {HttpClient} http HttpClient from @angular/common/http
 * @returns {IconService} The singleton instance or a new one
 */
export function ICON_SERVICE_PROVIDER_FACTORY(
  parentRegistry: IconService,
  http: HttpClient,
) {
  return parentRegistry || new IconService(http);
}

/**
 * Create a service provider that acts as a singleton.
 */
export const ICON_SERVICE_PROVIDER = {
  // If there is already an IconService available, use that.
  // Otherwise, provide a new one.
  provide: IconService,
  deps: [[new Optional(), new SkipSelf(), IconService], HttpClient],
  useFactory: ICON_SERVICE_PROVIDER_FACTORY,
};

/** Clones an SVGElement while preserving type information. */
function cloneSvg(svg: SVGElement) {
  return <SVGElement>svg.cloneNode(true);
}
